import { ChangeEvent } from '@faro/realtime-services';
import type { DefaultError, QueryKey } from '@tanstack/query-core';
import {
    QueryClient,
    useQuery,
    useQueryClient,
    UseQueryOptions,
    UseQueryResult,
    WithRequired,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export type UseRealtimeQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TEvent extends ChangeEvent<any> = ChangeEvent<any>,
> = WithRequired<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
    events: Observable<TEvent[]>;
    refetchWhen?: (event: TEvent, state: TQueryFnData | undefined) => boolean;
};

export type UseRealtimeQueryResult<TData = unknown, TError = DefaultError> = UseQueryResult<TData, TError> & {
    /**
     * Use this to optimistically update a query result when making mutations
     * Should only be used within useRealtimeMutation onMutate callback option
     * @example
     * ```
     * useRealtimeMutation({
     *   onMutate: () => {
     *     return myOtherQuery.setData(optimisticUpdate);
     *   }
     * })
     * ```
     * @param data
     */
    setData: (data: TData) => OptimisticUpdateContext;

    /**
     * Cancels in-flight data read-requests
     */
    cancel(): void;
};

export type OptimisticUpdateContext = {
    /**
     * Called in useRealtimeMutation on error to rollback optimistic updates
     */
    rollback: () => void;
};

/**
 * Custom react hook used to fetch data that should automatically refetch when the queried data changes.
 *
 * This just wraps ReactQuery's 'useQuery' hook with some standard and adds a few additional features:
 *   - refetchWhen: This hook accepts a `refetchWhen` configuration option that can be used to refetch the query data
 *     when certain events occur.
 *   - optimistic updates: This hook provides a `setData` function that can be used to optimistically update the query
 *     data.
 *   - cancel: This hook provides a `cancel` function that can be used to cancel in-flight read requests.
 *
 *
 * @param config                    The query configuration
 * @param config.events             Observable of {@link ChangeEvent}s that may trigger a refetch
 * @param config.refetchWhen        Optional function that determines if the query should refetch based on the event and current query data
 * @param localQueryClient          Optional query client override
 * @return UseRealtimeQueryResult   The {@link useQuery} hook extended with the additional features described above
 */
export function useRealtimeQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(
    config: UseRealtimeQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    localQueryClient?: QueryClient
): UseRealtimeQueryResult<TData, TError> {
    const { events, refetchWhen, ...rest } = config;

    const contextQueryClient = useQueryClient();
    const queryClient = localQueryClient ?? contextQueryClient;

    const cancel = () => {
        queryClient.cancelQueries({ queryKey: config.queryKey });
    };

    // Function used to optimistically update query data
    const setData = (value: TData) => {
        const rollbackData = queryClient.getQueryData(config.queryKey);

        // Cancel in-flight read requests to prevent stale data from rendering
        cancel();

        // Optimistically update the query data
        queryClient.setQueryData<TData>(config.queryKey, value);

        const rollback = () => {
            queryClient.setQueryData(config.queryKey, rollbackData);
        };

        return { rollback };
    };

    useEffect(() => {
        if (refetchWhen == null) {
            return;
        }

        const subscription = events
            .pipe(
                filter(events => {
                    const state = queryClient.getQueryData<TQueryFnData>(config.queryKey);
                    return events.some(event => refetchWhen?.(event, state));
                })
            )
            .subscribe(() => {
                queryClient.invalidateQueries({ queryKey: config.queryKey });
            });
        return () => {
            subscription.unsubscribe();
        };
    }, [refetchWhen]);

    const queryResult = useQuery(
        {
            staleTime: Infinity,
            // Covered by change event listener running on interval
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: 'always',
            ...rest,
        },
        queryClient
    );

    return {
        ...queryResult,
        setData,
        cancel,
    };
}
